"use client";

import { useCallback, useEffect, useState } from "react";
import { buildDefaultSmartFolders } from "@/lib/emailCleaner/defaultSmartFolders";
import { timeAgo } from "@/lib/emailCleaner/format";
import { countMatching, listSample } from "@/lib/emailCleaner/gmail";
import { smartFolderSearchQuery } from "@/lib/emailCleaner/queries";
import { smartFoldersStore } from "@/lib/emailCleaner/stores";
import { useViewStack } from "@/lib/emailCleaner/useViewStack";
import type { FolderMatchField, NormalizedEmail, SmartFolder } from "@/lib/emailCleaner/types";
import { Card, EmptyState, PrimaryButton, ScreenHeader } from "../Shared";

type View = { name: "list" } | { name: "detail"; folder: SmartFolder } | { name: "form"; existing: SmartFolder | null };

type AccountProps = { accessToken: string; accountEmail: string };
type Props = AccountProps & { onExit: () => void };

function loadFolders(): SmartFolder[] {
  const existing = smartFoldersStore.load();
  if (existing) return existing;
  const seeded = buildDefaultSmartFolders();
  smartFoldersStore.save(seeded);
  return seeded;
}

export default function FoldersTab({ accessToken, accountEmail, onExit }: Props) {
  const { view, depth, push, pop } = useViewStack<View>({ name: "list" });
  const back = depth > 1 ? pop : onExit;

  if (view.name === "form") {
    return <FormScreen existing={view.existing} onBack={back} onSaved={pop} />;
  }
  if (view.name === "detail") {
    return <DetailScreen accessToken={accessToken} accountEmail={accountEmail} folder={view.folder} onBack={back} onSettings={() => push({ name: "form", existing: view.folder })} />;
  }
  return (
    <ListScreen
      accessToken={accessToken}
      onBack={back}
      onOpen={(folder) => push({ name: "detail", folder })}
      onCreate={() => push({ name: "form", existing: null })}
    />
  );
}

function ListScreen({ accessToken, onBack, onOpen, onCreate }: { accessToken: string; onBack: () => void; onOpen: (f: SmartFolder) => void; onCreate: () => void }) {
  const [folders, setFolders] = useState<SmartFolder[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});

  const load = useCallback(() => {
    const list = loadFolders();
    setFolders(list);
    list.forEach((folder) => {
      const q = smartFolderSearchQuery(folder);
      if (!q) return;
      countMatching(accessToken, q)
        .then((count) => setCounts((prev) => ({ ...prev, [folder.id]: count })))
        .catch(() => {});
    });
  }, [accessToken]);

  useEffect(() => {
    // Loading folders + live counts on mount is the intended sync-with-Gmail effect here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  return (
    <div>
      <ScreenHeader
        title="Smart Folders"
        onBack={onBack}
        action={
          <button type="button" onClick={onCreate} className="rounded-full bg-violet-600 px-4 py-1.5 text-sm font-semibold text-white">
            + Create
          </button>
        }
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {folders.map((folder) => (
          <Card key={folder.id} onClick={() => onOpen(folder)}>
            <span className="text-2xl">{folder.icon}</span>
            <p className="mt-2 truncate text-sm font-medium text-slate-900 dark:text-white">{folder.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{counts[folder.id] ?? "…"} emails</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

function DetailScreen({
  accessToken,
  accountEmail,
  folder,
  onBack,
  onSettings,
}: AccountProps & { folder: SmartFolder; onBack: () => void; onSettings: () => void }) {
  const [messages, setMessages] = useState<NormalizedEmail[] | null>(null);
  const query = smartFolderSearchQuery(folder);

  useEffect(() => {
    if (!query) {
      // Clearing results when the folder has no match rule syncs state with that prop.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMessages([]);
      return;
    }
    listSample(accessToken, accountEmail, query, 50).then(setMessages);
  }, [accessToken, accountEmail, query]);

  return (
    <div>
      <ScreenHeader
        title={folder.name}
        onBack={onBack}
        action={
          <button type="button" onClick={onSettings} className="text-sm font-medium text-violet-600 dark:text-violet-400">
            Settings
          </button>
        }
      />
      {!messages && <p className="text-center text-sm text-slate-500 dark:text-slate-400">Loading…</p>}
      {messages && messages.length === 0 && <EmptyState>No emails match this folder yet</EmptyState>}
      {messages?.map((m) => (
        <Card key={m.id} className="mb-2 flex items-center justify-between">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">{m.from}</p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{m.subject || "(no subject)"}</p>
          </div>
          <span className="shrink-0 text-xs text-slate-400">{timeAgo(m.received_at)}</span>
        </Card>
      ))}
    </div>
  );
}

const ICON_CHOICES = ["📁", "🛍️", "🏦", "🍔", "🚕", "🎬", "✈️", "🧾", "💼", "👤", "🎓", "🏥", "🔒", "📰", "📄", "📅"];

function FormScreen({ existing, onBack, onSaved }: { existing: SmartFolder | null; onBack: () => void; onSaved: () => void }) {
  const [name, setName] = useState(existing?.name ?? "");
  const [icon, setIcon] = useState(existing?.icon ?? ICON_CHOICES[0]);
  const [matchField, setMatchField] = useState<FolderMatchField>(existing?.matchField ?? "from");
  const [values, setValues] = useState(existing?.matchValues.join(", ") ?? "");

  function save() {
    if (!name.trim()) return;
    const folder: SmartFolder = {
      id: existing?.id ?? crypto.randomUUID(),
      name: name.trim(),
      icon,
      accentIndex: existing?.accentIndex ?? 0,
      matchField,
      matchValues: values.split(",").map((s) => s.trim()).filter(Boolean),
    };
    const current = smartFoldersStore.load() ?? [];
    if (existing) {
      smartFoldersStore.save(current.map((f) => (f.id === folder.id ? folder : f)));
    } else {
      smartFoldersStore.save([...current, folder]);
    }
    onSaved();
  }

  function remove() {
    if (!existing) return;
    const current = smartFoldersStore.load() ?? [];
    smartFoldersStore.save(current.filter((f) => f.id !== existing.id));
    onSaved();
  }

  return (
    <div>
      <ScreenHeader title={existing ? "Folder Settings" : "Create Smart Folder"} onBack={onBack} />
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Folder Name</label>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
      />

      <p className="mt-4 text-sm font-medium text-slate-700 dark:text-slate-300">Icon</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {ICON_CHOICES.map((i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIcon(i)}
            className={`flex h-10 w-10 items-center justify-center rounded-xl border text-lg ${icon === i ? "border-violet-500 bg-violet-50 dark:bg-violet-500/10" : "border-slate-200 dark:border-slate-700"}`}
          >
            {i}
          </button>
        ))}
      </div>

      <p className="mt-4 text-sm font-medium text-slate-700 dark:text-slate-300">Conditions</p>
      <Card className="mt-2">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMatchField("from")}
            className={`flex-1 rounded-full py-1.5 text-xs font-semibold ${matchField === "from" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300"}`}
          >
            From contains
          </button>
          <button
            type="button"
            onClick={() => setMatchField("subject")}
            className={`flex-1 rounded-full py-1.5 text-xs font-semibold ${matchField === "subject" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300"}`}
          >
            Subject contains
          </button>
        </div>
        <input
          value={values}
          onChange={(e) => setValues(e.target.value)}
          placeholder="e.g. amazon.in, flipkart.com"
          className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
        />
      </Card>

      <div className="mt-5 flex flex-col gap-2">
        <PrimaryButton onClick={save}>Save Folder</PrimaryButton>
        {existing && (
          <button type="button" onClick={remove} className="rounded-full border border-red-300 py-2 text-sm font-semibold text-red-600 dark:border-red-700 dark:text-red-400">
            Delete Folder
          </button>
        )}
      </div>
    </div>
  );
}
